import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Droplets, BookOpen, ArrowRight, Microscope, Bell } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StaggerContainer } from "@/components/shared/animations";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cardVariants } from "@/lib/animations";
import { toast } from "sonner";
import { userApiClient } from "@/lib/userApiClient";
import { cn } from "@/lib/utils";

/**
 * 水质分析卡片的幽灵线框背景 - 波浪数据曲线
 */
function WaterAnalyticsGhostBg() {
  return (
    <svg
      className="absolute inset-0 w-full h-full opacity-[0.12] pointer-events-none"
      viewBox="0 0 200 100"
      preserveAspectRatio="none"
    >
      <path
        d="M0,50 Q25,30 50,50 T100,50 T150,50 T200,50"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        className="text-blue-400"
      />
      <path
        d="M0,65 Q25,45 50,65 T100,65 T150,65 T200,65"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.8"
        className="text-cyan-400"
      />
      <path
        d="M0,35 Q25,55 50,35 T100,35 T150,35 T200,35"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.6"
        className="text-blue-300"
      />
      <polygon
        points="170,20 180,15 190,20 190,30 180,35 170,30"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
        className="text-cyan-400"
      />
      <polygon
        points="20,70 30,65 40,70 40,80 30,85 20,80"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
        className="text-blue-400"
      />
    </svg>
  );
}

/**
 * 物种图谱卡片的幽灵线框背景 - 鱼轮廓 + DNA 螺旋
 */
function SpeciesWikiGhostBg() {
  return (
    <svg
      className="absolute inset-0 w-full h-full opacity-[0.12] pointer-events-none"
      viewBox="0 0 200 100"
      preserveAspectRatio="none"
    >
      <path
        d="M30,50 Q50,30 80,50 Q100,35 120,50 L140,40 L140,60 L120,50 Q100,65 80,50 Q50,70 30,50 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.8"
        className="text-purple-400"
      />
      <circle
        cx="50"
        cy="48"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
        className="text-purple-300"
      />
      <path
        d="M160,20 Q170,30 160,40 Q150,50 160,60 Q170,70 160,80"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.6"
        className="text-violet-400"
      />
      <path
        d="M175,20 Q165,30 175,40 Q185,50 175,60 Q165,70 175,80"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.6"
        className="text-purple-400"
      />
      <line
        x1="160"
        y1="30"
        x2="175"
        y2="30"
        stroke="currentColor"
        strokeWidth="0.4"
        className="text-purple-300"
      />
      <line
        x1="160"
        y1="50"
        x2="175"
        y2="50"
        stroke="currentColor"
        strokeWidth="0.4"
        className="text-purple-300"
      />
      <line
        x1="160"
        y1="70"
        x2="175"
        y2="70"
        stroke="currentColor"
        strokeWidth="0.4"
        className="text-purple-300"
      />
    </svg>
  );
}

/**
 * 状态徽章组件 - 暗色主题专用
 */
function StatusBadgeUI({
  status,
  label,
  pulseEffect = false,
}: {
  status: "available" | "coming-soon";
  label: string;
  pulseEffect?: boolean;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 font-medium",
        status === "available"
          ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
          : "bg-white/5 text-white/50 border-white/10",
      )}
    >
      {pulseEffect && status === "available" && (
        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
      )}
      {label}
    </Badge>
  );
}

/**
 * 上线提醒按钮组件 - 暗色主题专用
 */
function NotifyMeButton({ feature }: { feature: "water" | "species" }) {
  const { t } = useTranslation("home");
  const [showInput, setShowInput] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await userApiClient.post("/waitlist/subscribe", {
        waitlist: {
          email,
          campaign_slug:
            feature === "water" ? "water-analytics" : "species-wiki",
        },
      });
      setSubmitted(true);
      setTimeout(() => {
        setShowInput(false);
        setSubmitted(false);
        setEmail("");
      }, 2000);
    } catch (error: any) {
      console.error(`Failed to subscribe for ${feature}:`, error);
      toast.error(
        error.message || t("ecosystem.notifyMe.error") || "Failed to subscribe",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-8 flex items-center justify-end overflow-hidden">
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="submitted"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="text-xs text-emerald-400 flex items-center gap-1"
          >
            ✓ {t("ecosystem.notifyMe.success")}
          </motion.div>
        ) : showInput ? (
          <motion.form
            key="form"
            initial={{ opacity: 0, x: 20, width: 0 }}
            animate={{ opacity: 1, x: 0, width: "auto" }}
            exit={{ opacity: 0, x: 20, width: 0 }}
            onSubmit={handleSubmit}
            className="flex items-center gap-2"
          >
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("ecosystem.notifyMe.placeholder")}
              className="h-7 w-32 sm:w-48 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={isSubmitting}
              autoFocus
            />
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              className="h-7 px-2 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                t("ecosystem.notifyMe.submit")
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowInput(false)}
              className="h-7 w-7 p-0 text-white/40 hover:text-white/60 hover:bg-transparent"
              disabled={isSubmitting}
            >
              ✕
            </Button>
          </motion.form>
        ) : (
          <motion.div
            key="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowInput(true)}
              className="h-auto py-1 px-2 text-xs text-white/40 hover:text-white/60 hover:bg-transparent"
            >
              <Bell className="w-3 h-3 mr-1" />
              {t("ecosystem.notifyMe.label")}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * EcosystemGrid - 功能矩阵 Bento Grid 布局
 * 使用 shadcn/ui Card 组件 + Vercel 黑白主题适配
 */
export function EcosystemGrid() {
  const { t } = useTranslation("home");
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="py-12 sm:py-20 lg:py-32 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* Card A: AI Health Assessment - 大卡片 */}
          <motion.div
            className="md:row-span-2"
            variants={prefersReducedMotion ? undefined : cardVariants}
          >
            <Link to="/assessments" className="block h-full group">
              <Card
                className={cn(
                  "h-full min-h-[360px] sm:min-h-[400px] md:min-h-full",
                  "bg-white/5 backdrop-blur-sm",
                  "border border-white/10",
                  "shadow-sm hover:shadow-2xl hover:shadow-black/20",
                  "hover:bg-white/10 hover:border-white/20",
                  "transition-all duration-300",
                )}
              >
                <CardContent className="p-5 sm:p-6 md:p-8 h-full flex flex-col">
                  <div className="mb-4">
                    <StatusBadgeUI
                      status="available"
                      label={t("ecosystem.cards.assessment.tag")}
                      pulseEffect
                    />
                  </div>

                  <div className="w-12 h-12 sm:w-14 md:w-16 sm:h-14 md:h-16 rounded-xl sm:rounded-2xl bg-white/10 flex items-center justify-center mb-4 sm:mb-6">
                    <Microscope className="w-6 h-6 sm:w-7 md:w-8 sm:h-7 md:h-8 text-white/70" />
                  </div>

                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3">
                    {t("ecosystem.cards.assessment.title")}
                  </h3>

                  <p className="text-white/60 text-base sm:text-lg mb-4 sm:mb-6 flex-grow">
                    {t("ecosystem.cards.assessment.description")}
                  </p>

                  {/* 产品截图 - 带 UI 扫描框覆盖层 */}
                  <div className="relative rounded-lg sm:rounded-xl overflow-hidden bg-white/5 aspect-video mb-4 sm:mb-6 group-hover:scale-[1.02] transition-transform duration-300">
                    <img
                      src="/images/main-image.jpg"
                      alt="AI Assessment Preview"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    {/* UI 扫描框覆盖层 - 增强的AI分析界面 */}
                    <div className="absolute inset-0 pointer-events-none">
                      {/* 绿色对焦框 */}
                      <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-emerald-400/80" />
                      <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-emerald-400/80" />
                      <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-emerald-400/80" />
                      <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-emerald-400/80" />

                      {/* 水平扫描线 */}
                      <motion.div
                        className="absolute left-4 right-4 h-0.5 bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent"
                        initial={{ top: "15%" }}
                        animate={{ top: ["15%", "85%", "15%"] }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />

                      {/* 垂直扫描线 */}
                      <motion.div
                        className="absolute top-4 bottom-4 w-0.5 bg-gradient-to-b from-transparent via-emerald-400/40 to-transparent"
                        initial={{ left: "20%" }}
                        animate={{ left: ["20%", "80%", "20%"] }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 1,
                        }}
                      />

                      {/* 数据网格线 */}
                      <div className="absolute inset-0 opacity-20">
                        <svg className="w-full h-full" viewBox="0 0 400 200">
                          <defs>
                            <pattern
                              id="grid"
                              width="20"
                              height="20"
                              patternUnits="userSpaceOnUse"
                            >
                              <path
                                d="M 20 0 L 0 0 0 20"
                                fill="none"
                                stroke="rgb(16, 185, 129)"
                                strokeWidth="0.5"
                                opacity="0.3"
                              />
                            </pattern>
                          </defs>
                          <rect width="100%" height="100%" fill="url(#grid)" />
                        </svg>
                      </div>

                      {/* 分析状态指示器 */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full border border-emerald-400/40">
                        <span className="text-xs text-emerald-400 font-medium flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                          {t("ecosystem.cards.assessment.analyzing")}
                        </span>
                      </div>

                      {/* 数据流线条 */}
                      <div className="absolute top-8 right-8 w-16 h-16 opacity-30">
                        <svg viewBox="0 0 64 64" className="w-full h-full">
                          <path
                            d="M8,32 L24,16 L40,32 L56,16"
                            fill="none"
                            stroke="rgb(16, 185, 129)"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="animate-pulse"
                          />
                          <circle
                            cx="8"
                            cy="32"
                            r="2"
                            fill="rgb(16, 185, 129)"
                            className="animate-ping"
                          />
                          <circle
                            cx="56"
                            cy="16"
                            r="2"
                            fill="rgb(16, 185, 129)"
                            className="animate-ping"
                            style={{ animationDelay: "0.5s" }}
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center text-white font-medium group-hover:text-emerald-400 transition-colors duration-300">
                    {t("ecosystem.cards.assessment.cta")}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>

          {/* Card B: Water Analytics */}
          <motion.div
            variants={prefersReducedMotion ? undefined : cardVariants}
          >
            <div className="relative h-full min-h-[160px] sm:min-h-[180px] rounded-xl p-[1px] bg-gradient-to-br from-blue-500/30 via-transparent to-cyan-500/20">
              <Card
                className={cn(
                  "h-full",
                  "bg-white/5 backdrop-blur-sm",
                  "border-0 rounded-xl",
                )}
              >
                <CardContent className="p-4 sm:p-6 h-full relative overflow-hidden">
                  <WaterAnalyticsGhostBg />

                  <div className="relative z-10 mb-4 flex items-center justify-between">
                    <StatusBadgeUI
                      status="coming-soon"
                      label={t("ecosystem.cards.waterAnalytics.tag")}
                    />
                    <NotifyMeButton feature="water" />
                  </div>

                  <div className="relative z-10 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <div className="absolute inset-0 bg-blue-500/20 rounded-xl blur-md" />
                    <div className="absolute inset-0 bg-blue-400/10 rounded-xl animate-pulse" />
                    <div className="relative w-full h-full rounded-xl bg-white/10 flex items-center justify-center shadow-[0_0_10px_rgba(59,130,246,0.3)]">
                      <Droplets className="w-6 h-6 text-blue-400 drop-shadow-[0_0_4px_rgba(59,130,246,0.5)]" />
                    </div>
                  </div>

                  <h3 className="relative z-10 text-xl font-bold text-white/80 mb-2">
                    {t("ecosystem.cards.waterAnalytics.title")}
                  </h3>

                  <p className="relative z-10 text-white/50 text-sm">
                    {t("ecosystem.cards.waterAnalytics.description")}
                  </p>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Card C: Species Wiki */}
          <motion.div
            variants={prefersReducedMotion ? undefined : cardVariants}
          >
            <div className="relative h-full min-h-[160px] sm:min-h-[180px] rounded-xl p-[1px] bg-gradient-to-br from-purple-500/30 via-transparent to-violet-500/20">
              <Card
                className={cn(
                  "h-full",
                  "bg-white/5 backdrop-blur-sm",
                  "border-0 rounded-xl",
                )}
              >
                <CardContent className="p-4 sm:p-6 h-full relative overflow-hidden">
                  <SpeciesWikiGhostBg />

                  <div className="relative z-10 mb-4 flex items-center justify-between">
                    <StatusBadgeUI
                      status="coming-soon"
                      label={t("ecosystem.cards.speciesWiki.tag")}
                    />
                    <NotifyMeButton feature="species" />
                  </div>

                  <div className="relative z-10 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <div className="absolute inset-0 bg-purple-500/20 rounded-xl blur-md" />
                    <div
                      className="absolute inset-0 bg-purple-400/10 rounded-xl animate-pulse"
                      style={{ animationDelay: "1s" }}
                    />
                    <div className="relative w-full h-full rounded-xl bg-white/10 flex items-center justify-center shadow-[0_0_10px_rgba(147,51,234,0.3)]">
                      <BookOpen className="w-6 h-6 text-purple-400 drop-shadow-[0_0_4px_rgba(147,51,234,0.5)]" />
                    </div>
                  </div>

                  <h3 className="relative z-10 text-xl font-bold text-white/80 mb-2">
                    {t("ecosystem.cards.speciesWiki.title")}
                  </h3>

                  <p className="relative z-10 text-white/50 text-sm">
                    {t("ecosystem.cards.speciesWiki.description")}
                  </p>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </StaggerContainer>
      </div>
    </section>
  );
}

export default EcosystemGrid;
